import React, { useMemo, useState } from "react";
import { Button, Confirm, Icon } from "semantic-ui-react";
import { saveAs } from "file-saver";
import { useLanguage } from "../../../../contexts";
import { P, motor, useEnergia, useGuardarSeccion } from "../engine";
import { useBorrador } from "../common/useBorrador";
import { useEvidencias } from "../common/useEvidencias";
import { Notice, SaveBar, SaveErrors, StatusBadge } from "../common/SectionCommon";
import { GraficoCatalogo } from "../common/Grafico";
import "../common/common.scss";
import "./DocumentChecklist.scss";

const tomar = (inst) => ({ docCheck: { ...(inst.docCheck || {}) } });
const fmt = (v, d = 0) => P.fmt(v, d);
const hoy = () => new Date().toISOString().slice(0, 10);
const ESTADOS = [["si", "energy_performance_yes"], ["parcial", "energy_performance_partial"], ["no", "energy_performance_no"]];

function SubirEvidencia({ tipo, deshabilitado, onSubir, t }) {
  const [fecha, setFecha] = useState(hoy());
  const [archivo, setArchivo] = useState(null);
  return (
    <div className="ep-docs__upload">
      <input type="file" aria-label={`${t("energy_performance_evidence_file")} ${tipo}`} disabled={deshabilitado} onChange={(e) => setArchivo(e.target.files && e.target.files[0])} />
      <input type="date" aria-label={`${t("energy_performance_evidence_date")} ${tipo}`} value={fecha} disabled={deshabilitado} onChange={(e) => setFecha(e.target.value)} />
      <Button type="button" size="mini" basic disabled={deshabilitado || !archivo || !fecha} onClick={async () => { if (await onSubir(archivo, fecha)) setArchivo(null); }}>
        <Icon name="upload" /> {t("energy_performance_evidence_upload")}
      </Button>
    </div>
  );
}

// Datos y documentos necesarios — 47 tipos (hoja 06, hoja 27; hoja 01 filas 45, 92, 143, 156).
export function DocumentChecklist({ installation, onSaved }) {
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const { archivo, inst, f, error } = useEnergia(installation);
  const { borrador: b, cambiar, dirty, descartar } = useBorrador(inst, tomar);
  const { guardar, saving, errores } = useGuardarSeccion(installation, onSaved, { t });
  const evid = useEvidencias(guardar, t, installation?.site);
  const [aBorrar, setABorrar] = useState(null);

  const vivo = useMemo(() => (archivo && b ? motor.calcularVista({ ...archivo, instalacion: { ...inst, ...b } }) : null), [archivo, inst, b]);
  const fv = (vivo && vivo.f) || f;

  if (!installation) return <p>{t("energy_performance_create_profile_first")}</p>;
  if (!b) return null;
  if (!fv) return <Notice tipo="danger" titulo={t("energy_performance_data_invalid")}>{error}</Notice>;

  const res = P.docResumen(fv);
  const porCat = {};
  res.all.forEach((r) => { (porCat[r.d.cat] = porCat[r.d.cat] || []).push(r); });
  const docs = inst.docs || [];
  const color = res.pct >= P.SEM_V ? "ok" : res.pct >= P.SEM_A ? "warn" : "danger";

  // tocar el estado activo lo devuelve al automático (por evidencia o por los datos)
  const marcar = (tipo, valor) => {
    const dc = { ...b.docCheck };
    if (dc[tipo] === valor) delete dc[tipo]; else dc[tipo] = valor;
    cambiar("docCheck", dc);
  };
  const subir = (tipo) => (file, fecha) => evid.subir(file, ({ nombre, archivo: a }) => ({ docs: [...docs, { tipo, nombre, fecha, archivo: a }] }));
  const confirmarBorrado = async () => {
    const d = aBorrar;
    setABorrar(null);
    await evid.borrar(d.archivo, { docs: docs.filter((x) => x !== d) });
  };
  const pedido = () => saveAs(new Blob([P.generarPedidoDocs(fv)], { type: "text/plain;charset=utf-8" }), `pedido_documentos_${installation.code}.txt`);

  return (
    <div className="ep-screen ep-docs">
      <h3 className="ep-screen__title">{t("energy_performance_docs_title")}</h3>
      <p className="ep-screen__intro">{t("energy_performance_docs_intro")}</p>

      <div className="ep-kpis">
        <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_docs_availability")}</div><div className={`ep-kpi__value ep-docs__pct--${color}`}>{fmt(res.pct, 0)}%</div><div className="ep-kpi__sub">{res.si} {t("energy_performance_yes").toLowerCase()} · {res.par} {t("energy_performance_partial").toLowerCase()} · {res.no} {t("energy_performance_no").toLowerCase()} · {res.total} {t("energy_performance_docs_applicable")}</div></div>
        <div className="ep-kpi"><div className="ep-kpi__label">{t("energy_performance_docs_critical_missing")}</div><div className={`ep-kpi__value ep-docs__pct--${res.critFalt.length ? "danger" : "ok"}`}>{res.critFalt.length}</div></div>
      </div>
      <div className="ep-pills">
        <Button type="button" basic size="small" onClick={pedido}><Icon name="file alternate outline" /> {t("energy_performance_docs_request")}</Button>
      </div>
      <div className="ep-charts"><GraficoCatalogo f={fv} id="documentos" t={t} /></div>
      {dirty && <Notice tipo="info">{t("energy_performance_docs_save_first")}</Notice>}

      {Object.entries(porCat).map(([cat, items]) => (
        <section className="ep-section" key={cat}>
          <h4 className="ep-section__title">{cat}</h4>
          <div className="ep-table">
            <table>
              <thead>
                <tr><th>{t("energy_performance_docs_document")}</th><th>{t("energy_performance_docs_why")}</th><th>{t("energy_performance_docs_period")}</th><th>{t("energy_performance_docs_is_there")}</th><th>{t("energy_performance_docs_evidence")}</th></tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const manual = b.docCheck[r.d.tipo];
                  return (
                    <tr key={r.idx} className={r.estado === "na" ? "ep-docs__na" : r.d.crit && r.estado !== "si" ? "ep-docs__crit" : undefined}>
                      <td><strong>{r.d.doc}</strong>{r.d.crit && <> <StatusBadge estado="Brecha mayor">{t("energy_performance_docs_critical")}</StatusBadge></>}</td>
                      <td className="ep-section__note">{r.d.por}</td>
                      <td className="ep-section__note">{r.d.per}</td>
                      <td>
                        {r.estado === "na" ? <StatusBadge estado="Pendiente">{t("energy_performance_docs_not_applicable")}</StatusBadge> : (
                          <>
                            <div className="ep-docs__seg" role="group" aria-label={`${t("energy_performance_docs_is_there")} — ${r.d.doc}`}>
                              {ESTADOS.map(([v, k]) => (
                                <button key={v} type="button" aria-pressed={r.estado === v} className={r.estado === v ? `on-${v}` : ""} onClick={() => marcar(r.d.tipo, v)}>{t(k)}</button>
                              ))}
                            </div>
                            <div className="ep-section__note">{manual ? t("energy_performance_docs_manual") : t("energy_performance_docs_auto")}</div>
                          </>
                        )}
                      </td>
                      <td className="ep-docs__evidence">
                        {r.ev.map((e, j) => {
                          const d = docs.find((x) => x.tipo === e.tipo && x.nombre === e.nombre && x.fecha === e.fecha);
                          return (
                            <div key={j} className="ep-docs__file">
                              ✓ {d && d.archivo ? <button type="button" className="ep-link" onClick={() => evid.ver(d.archivo)}>{e.nombre}</button> : e.nombre} ({e.fecha})
                              {d && <button type="button" className="ep-docs__del" aria-label={`${t("energy_performance_delete")} ${e.nombre}`} disabled={dirty || evid.ocupado} onClick={() => setABorrar(d)}>×</button>}
                            </div>
                          );
                        })}
                        {!r.ev.length && <div className="ep-section__note">{t("energy_performance_docs_no_evidence")}</div>}
                        {r.estado !== "na" && <SubirEvidencia tipo={r.d.tipo} deshabilitado={dirty || evid.ocupado} onSubir={subir(r.d.tipo)} t={t} />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>
      ))}

      <SaveErrors errores={errores} t={t} />
      <SaveBar dirty={dirty} invalidos={0} saving={saving} onSave={() => guardar({ docCheck: b.docCheck })} onDiscard={descartar} t={t} />

      <Confirm
        open={aBorrar != null}
        content={t("energy_performance_docs_confirm_delete")}
        cancelButton={t("cancel")}
        confirmButton={t("energy_performance_delete")}
        onCancel={() => setABorrar(null)}
        onConfirm={confirmarBorrado}
      />
    </div>
  );
}
